import type { Vault, } from "@bracket/prisma";
import type { TokenDefinition, } from "@bracket/chainography";
import type {
  BrktUSD,
} from "@bracket/types";

import type {
  ColateralBalances,
} from "@types";


declare type EpochSeconds = number;
declare type SecondsDuration = number;

declare type CachedBigInt = {
  time: EpochSeconds;
  value: bigint;
  since: SecondsDuration;
}

declare type CachedNumber = {
  time: EpochSeconds;
  value: number;
  since: SecondsDuration;
}

declare type CachedType<T extends object> = {
  time: EpochSeconds;
  value: T;
  since: SecondsDuration;
	promised?: Promise<SecondsDuration>|null;
}

declare type TypedCache = {
  bracket_rate: CachedNumber;
  bracket_price: CachedNumber;
  bracket_supply: CachedBigInt;
  latest_price_eth: CachedNumber;
  latest_price_btc: CachedNumber;
  latest_price_usd: CachedNumber;
  platform_tvl: CachedType<BrktUSD>;
  total_value: CachedBigInt;
  balance_of: CachedBigInt;
  vaults: Record<Vault["address"], TypedVaultCache>;
  tokens: Record<TokenDefinition["symbol"], TypedTokenCache>;
}

declare type TypedVaultCache = {
  total_supply: CachedBigInt;
  assets_per_share: CachedBigInt;
  shares_of: CachedBigInt;
  last_deposit: CachedType<{ epoch: number; assets: bigint; }>;
  epoch: CachedNumber;
  vanity_nav: CachedBigInt;
  balance_of: CachedBigInt;
  total_deposits: CachedBigInt;
	total_shares: CachedBigInt;
  total_active_shares: CachedBigInt;
	total_pending_shares: CachedBigInt;
  pending_deposit_shares: CachedBigInt;
  total_withdrawals: CachedBigInt;
  vault_variance: Record<number, CachedBigInt>; // epoch -> variance, we have to cache each epoch
  est_amount_due: CachedBigInt;
  strategy_tvl: CachedType<BrktUSD>;
  shares_outstanding: CachedBigInt;
  pending_deposits: CachedBigInt;
  accrued_manager_performance_fees: CachedBigInt;
  accrued_manager_tvl_fees: CachedBigInt;
  accrued_brkt_tvl_fees: CachedBigInt;
	nav_fees: CachedBigInt;
}

declare type TypedTokenCache = {
  balance_of: CachedBigInt;
  colateral_info: CachedType<ColateralBalances>;
  conv_rate: CachedNumber;
}

export const initialCache: TypedCache = {
  bracket_rate: {
    time: 60_000,
    value: 0,
    since: 0,
  },
  bracket_price: {
    time: 60_000,
    value: 0,
    since: 0,
  },
  bracket_supply: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  latest_price_eth: {
    time: 60_000,
    value: 0,
    since: 0,
  },
  latest_price_btc: {
    time: 60_000,
    value: 0,
    since: 0,
  },
  latest_price_usd: {
    time: 60_000,
    value: 0,
    since: 0,
  },
  platform_tvl: {
    time: 60_000,
    value: {
      usd: 0,
      brkt: "0",
    },
    since: 0,
  }, 
  total_value: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  balance_of: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  vaults: {},
  tokens: {},
}

export const initialVaultCache: TypedVaultCache = {
  epoch: {
    time: 60_000,
    value: 0,
    since: 0,
  },
  total_supply: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  assets_per_share: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  shares_of: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  last_deposit: {
    time: 60_000,
    value: { epoch: 0, assets: 0n, },
    since: 0,
  },
  vanity_nav: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  balance_of: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  total_deposits: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  total_active_shares: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
	total_pending_shares: {
		time: 60_000,
		value: 0n,
		since: 0,
	},
	total_shares: {
		time: 60_000,
		value: 0n,
		since: 0,
	},
  pending_deposit_shares: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  total_withdrawals: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  vault_variance: { },
  est_amount_due: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  strategy_tvl: {
    time: 60_000,
    value: {
      brkt: "0",
      usd: 0,
    },
    since: 0,
		promised: null,
  },
  shares_outstanding: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  pending_deposits: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  accrued_manager_performance_fees: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  accrued_manager_tvl_fees: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  accrued_brkt_tvl_fees: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
	nav_fees: {
		time: 15_000,
		value: 0n,
		since: 0,
	},
};

export const initialTokenCache: TypedTokenCache = {
  balance_of: {
    time: 60_000,
    value: 0n,
    since: 0,
  },
  colateral_info: {
    time: 60_000,
    value: {
      title: "",
      symbol: "",
      value: 0n,
      count: 0,
      label: "0",
      rate: 0,
      completePercentage: 0,
    },
    since: 0,
  },
  conv_rate: {
    time: 60_000,
    value: 0,
    since: 0,
  },
};

export const reset = () => {
  cache = initialCache;
}

export let cache: TypedCache = initialCache;

globalThis.brkt_cache = cache;
